import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#1E40AF', '#16A34A', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#DB2777', '#059669', '#F97316', '#0284C7'];

const RANGE_OPTIONS = [
  { key: 'all',        label: 'All-time' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_30d',   label: 'Last 30 days' },
  { key: 'last_90d',   label: 'Last 90 days' },
  { key: 'this_year',  label: 'This year (YTD)' },
];
const CATEGORY_OPTIONS = [
  { key: '',                     label: 'All categories' },
  { key: 'life_insurance',       label: 'Insurance · Life' },
  { key: 'general_insurance',    label: 'Insurance · General' },
  { key: 'investment',           label: 'Investment' },
  { key: 'loans',                label: 'Loans' },
];

const Reports = () => {
  const [overview, setOverview] = useState(null);
  const [clientReport, setClientReport] = useState(null);
  const [newBiz, setNewBiz] = useState(null);
  const [renewed, setRenewed] = useState(null);
  const [lost, setLost] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [lostAnalysis, setLostAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [category, setCategory] = useState('');
  const [insurer, setInsurer] = useState('');
  const [productType, setProductType] = useState('');
  const [conversionWeeks, setConversionWeeks] = useState(12);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range && range !== 'all') params.range = range;
      if (category) params.category = category;
      if (insurer) params.insurer = insurer;
      if (productType) params.product_type = productType;
      const [ov, cl, nb, rn, ls, cv, la] = await Promise.all([
        axios.get(`${API}/reports/overview`,     { withCredentials: true, params }),
        axios.get(`${API}/reports/clients`,      { withCredentials: true }),
        axios.get(`${API}/reports/new-business`, { withCredentials: true, params }),
        axios.get(`${API}/reports/renewed`,      { withCredentials: true, params }),
        axios.get(`${API}/reports/lost`,         { withCredentials: true, params }),
        axios.get(`${API}/reports/leads-vs-conversion`, { withCredentials: true, params: { weeks: conversionWeeks } }),
        axios.get(`${API}/reports/lost-leads-analysis`, { withCredentials: true }),
      ]);
      setOverview(ov.data);
      setClientReport(cl.data);
      setNewBiz(nb.data);
      setRenewed(rn.data);
      setLost(ls.data);
      setConversion(cv.data);
      setLostAnalysis(la.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  }, [range, category, insurer, productType, conversionWeeks]);
  useEffect(() => { load(); }, [load]);

  const insurerOptions = useMemo(() => {
    const list = (overview?.by_insurer || []).map((r) => r.key).filter(k => k && k !== '-');
    return ['', ...list];
  }, [overview]);
  const productTypeOptions = useMemo(() => {
    const list = (overview?.by_product_type || []).map((r) => r.key).filter(k => k && k !== '-');
    return ['', ...list];
  }, [overview]);

  if (loading || !overview) return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;

  const t = overview.totals || {};

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Reports & Analytics</h1>
        <p className="text-slate-600 mt-1">Cross-filter your book by time, category, insurer and product type.</p>
      </div>

      {/* Cross-filter bar */}
      <Card className="border-slate-200" data-testid="reports-filters">
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Time range</Label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="filter-range"><SelectValue /></SelectTrigger>
              <SelectContent>{RANGE_OPTIONS.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={category || '_all'} onValueChange={(v) => setCategory(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All categories</SelectItem>
                {CATEGORY_OPTIONS.filter(c => c.key).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Insurer</Label>
            <Select value={insurer || '_all'} onValueChange={(v) => setInsurer(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-insurer"><SelectValue placeholder="All insurers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All insurers</SelectItem>
                {insurerOptions.filter(Boolean).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Product type</Label>
            <Select value={productType || '_all'} onValueChange={(v) => setProductType(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-product-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All types</SelectItem>
                {productTypeOptions.filter(Boolean).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reports-totals">
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Records</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{t.records || 0}</p>
        </div>
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Premium</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{formatCurrency(t.premium || 0)}</p>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">GST</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{formatCurrency(t.gst || 0)}</p>
        </div>
        <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Sum Assured</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{formatCurrency(t.sum_assured || 0)}</p>
        </div>
      </div>

      <Tabs defaultValue="category">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="category"     data-testid="tab-by-category">By Category</TabsTrigger>
          <TabsTrigger value="insurer"      data-testid="tab-by-insurer">By Insurer</TabsTrigger>
          <TabsTrigger value="product-type" data-testid="tab-by-product-type">By Product Type</TabsTrigger>
          <TabsTrigger value="trend"        data-testid="tab-trend">Monthly Trend</TabsTrigger>
          <TabsTrigger value="new-biz"      data-testid="tab-new-biz">New Business</TabsTrigger>
          <TabsTrigger value="renewed"      data-testid="tab-renewed">Renewed</TabsTrigger>
          <TabsTrigger value="lost"         data-testid="tab-lost">Lost</TabsTrigger>
          <TabsTrigger value="conversion"   data-testid="tab-conversion">Leads vs Conversion</TabsTrigger>
          <TabsTrigger value="lost-analysis" data-testid="tab-lost-analysis">Lost Leads Analysis</TabsTrigger>
          <TabsTrigger value="clients"      data-testid="tab-clients-report">Clients</TabsTrigger>
        </TabsList>

        <TabsContent value="category" className="mt-4 space-y-4">
          <Card className="border-slate-200"><CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Records by category</CardTitle></CardHeader>
            <CardContent>
              {overview.by_category.length === 0 ? <p className="text-center text-slate-500 py-6">No matching records</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={overview.by_category}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="key" /><YAxis /><Tooltip /><Legend />
                    <Bar dataKey="records" name="Records" fill="#1E40AF" /><Bar dataKey="premium" name="Premium ₹" fill="#16A34A" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insurer" className="mt-4 space-y-4">
          <Card className="border-slate-200"><CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Top insurers (records + premium)</CardTitle></CardHeader>
            <CardContent>
              {overview.by_insurer.length === 0 ? <p className="text-center text-slate-500 py-6">No matching records</p> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Insurer</TableHead><TableHead className="text-right">Records</TableHead><TableHead className="text-right">Premium</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {overview.by_insurer.map((r) => (
                      <TableRow key={r.key} data-testid={`insurer-row-${r.key}`}>
                        <TableCell className="font-medium">{r.key}</TableCell>
                        <TableCell className="text-right">{r.records}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.premium)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="product-type" className="mt-4 space-y-4">
          <Card className="border-slate-200"><CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Product-type mix</CardTitle></CardHeader>
            <CardContent>
              {overview.by_product_type.length === 0 ? <p className="text-center text-slate-500 py-6">No matching records</p> : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={overview.by_product_type} cx="50%" cy="50%" outerRadius={110} label dataKey="records" nameKey="key">
                      {overview.by_product_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="mt-4 space-y-4">
          <Card className="border-slate-200"><CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Monthly new records + premium</CardTitle></CardHeader>
            <CardContent>
              {overview.trend_monthly.length === 0 ? <p className="text-center text-slate-500 py-6">No matching records</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={overview.trend_monthly}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" /><YAxis yAxisId="left" /><YAxis yAxisId="right" orientation="right" /><Tooltip /><Legend />
                    <Line yAxisId="left" type="monotone" dataKey="records" stroke="#1E40AF" strokeWidth={2} name="Records" />
                    <Line yAxisId="right" type="monotone" dataKey="premium" stroke="#16A34A" strokeWidth={2} name="Premium ₹" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="new-biz" className="mt-4 space-y-4">
          <BusinessReport data={newBiz} accent="emerald" emptyMsg="No new business in this window." testPrefix="new-biz" />
        </TabsContent>

        <TabsContent value="renewed" className="mt-4 space-y-4">
          <BusinessReport data={renewed} accent="blue" emptyMsg="No renewed business in this window. Renewals get counted once a policy is marked renewed on the Renewals page." testPrefix="renewed" />
        </TabsContent>

        <TabsContent value="lost" className="mt-4 space-y-4">
          <BusinessReport data={lost} accent="red" emptyMsg="No lost business in this window. Set a policy to 'Lapsed' or a lead to 'Lost' to see it here." testPrefix="lost" showSourceColumn />
        </TabsContent>

        <TabsContent value="conversion" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Leads vs Conversion — weekly</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Weekly buckets align to Monday. Conversion rate = converted / created across the whole window.</p>
              </div>
              <div>
                <Label className="text-xs">Window</Label>
                <Select value={String(conversionWeeks)} onValueChange={(v) => setConversionWeeks(parseInt(v, 10))}>
                  <SelectTrigger className="w-40" data-testid="conversion-window-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">Last 4 weeks</SelectItem>
                    <SelectItem value="8">Last 8 weeks</SelectItem>
                    <SelectItem value="12">Last 12 weeks</SelectItem>
                    <SelectItem value="26">Last 26 weeks</SelectItem>
                    <SelectItem value="52">Last 52 weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {!conversion ? <p className="text-center text-slate-500 py-6">Loading…</p> : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4" data-testid="conversion-kpis">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Leads created</p><p className="text-2xl font-bold text-blue-800">{conversion.totals.created}</p></div>
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Leads converted</p><p className="text-2xl font-bold text-emerald-800">{conversion.totals.converted}</p></div>
                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Conversion rate</p><p className="text-2xl font-bold text-purple-800">{conversion.totals.conversion_rate_percent}%</p></div>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={conversion.series} data-testid="conversion-chart">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="week_start" tickFormatter={(d) => (d || '').slice(5)} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="created"   name="Created"   stroke="#1E40AF" strokeWidth={2} />
                      <Line type="monotone" dataKey="converted" name="Converted" stroke="#16A34A" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lost-analysis" className="mt-4 space-y-4">
          {!lostAnalysis ? <p className="text-center text-slate-500 py-6">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lost-analysis-kpis">
                <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Total lost</p><p className="text-2xl font-bold text-red-800">{lostAnalysis.totals.total_lost}</p></div>
                <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Policies lapsed</p><p className="text-2xl font-bold text-amber-800">{lostAnalysis.totals.policies_lapsed}</p></div>
                <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Leads lost</p><p className="text-2xl font-bold text-purple-800">{lostAnalysis.totals.leads_lost}</p></div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Won back</p><p className="text-2xl font-bold text-emerald-800">{lostAnalysis.totals.won_back}</p></div>
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3"><p className="text-[11px] uppercase text-slate-500">Win-back rate</p><p className="text-2xl font-bold text-blue-800">{lostAnalysis.totals.winback_rate_percent}%</p></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">By Product Type</CardTitle></CardHeader>
                  <CardContent>
                    {lostAnalysis.by_product_type.length === 0 ? <p className="text-center text-slate-500 py-6">No data</p> : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={lostAnalysis.by_product_type} data-testid="lost-by-type-chart">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="key" /><YAxis allowDecimals={false} /><Tooltip />
                          <Bar dataKey="count" name="Lost" fill="#DC2626" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">By Source</CardTitle></CardHeader>
                  <CardContent>
                    {lostAnalysis.by_source.length === 0 ? <p className="text-center text-slate-500 py-6">No data</p> : (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart data-testid="lost-by-source-chart">
                          <Pie data={lostAnalysis.by_source} dataKey="count" nameKey="key" innerRadius={40} outerRadius={90} label>
                            {lostAnalysis.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip /><Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="border-slate-200">
                <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">Top loss reasons</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Populated when you enter a reason at "Mark as Lost".</p>
                </CardHeader>
                <CardContent>
                  {lostAnalysis.by_reason.length === 0 ? (
                    <p className="text-center text-slate-500 py-6" data-testid="lost-reasons-empty">No reasons captured yet — the next time you mark a lead or policy lost, jot down a reason and it'll show up here.</p>
                  ) : (
                    <Table data-testid="lost-reasons-table">
                      <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {lostAnalysis.by_reason.map((r) => (
                          <TableRow key={r.key}><TableCell>{r.key}</TableCell><TableCell className="text-right font-medium">{r.count}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="clients" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200"><CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Total Clients</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold text-blue-800">{clientReport?.total_clients || 0}</p></CardContent></Card>
            <Card className="border-slate-200"><CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Individual</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold text-blue-600">{clientReport?.individual_count || 0}</p></CardContent></Card>
            <Card className="border-slate-200"><CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Corporate</CardTitle></CardHeader><CardContent><p className="text-4xl font-bold text-purple-600">{clientReport?.corporate_count || 0}</p></CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ACCENTS = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800' },
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800' },
};

const BusinessReport = ({ data, accent, emptyMsg, testPrefix, showSourceColumn }) => {
  if (!data) return null;
  const acc = ACCENTS[accent] || ACCENTS.blue;
  const rows = data.rows || [];
  return (
    <div className="space-y-4" data-testid={`${testPrefix}-panel`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`rounded-lg ${acc.bg} ${acc.border} border px-4 py-3`}>
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Records</p>
          <p className={`text-2xl font-bold ${acc.text}`} style={{ fontFamily: 'Manrope' }}>{data.totals?.count || 0}</p>
        </div>
        <div className={`rounded-lg ${acc.bg} ${acc.border} border px-4 py-3`}>
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Premium Total</p>
          <p className={`text-2xl font-bold ${acc.text}`} style={{ fontFamily: 'Manrope' }}>{formatCurrency(data.totals?.premium_total || 0)}</p>
        </div>
        {data.counts && (
          <>
            <div className={`rounded-lg ${acc.bg} ${acc.border} border px-4 py-3`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Policies lapsed</p>
              <p className={`text-2xl font-bold ${acc.text}`} style={{ fontFamily: 'Manrope' }}>{data.counts.policies_lapsed}</p>
            </div>
            <div className={`rounded-lg ${acc.bg} ${acc.border} border px-4 py-3`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Leads lost</p>
              <p className={`text-2xl font-bold ${acc.text}`} style={{ fontFamily: 'Manrope' }}>{data.counts.leads_lost}</p>
            </div>
          </>
        )}
      </div>
      {rows.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="py-8 text-center text-slate-500" data-testid={`${testPrefix}-empty`}>{emptyMsg}</CardContent></Card>
      ) : (
        <Card className="border-slate-200">
          <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Records ({rows.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                {showSourceColumn && <TableHead>Source</TableHead>}
                <TableHead>Name</TableHead>
                <TableHead>Insurer</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Premium</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.slice(0, 200).map((r, i) => (
                  <TableRow key={`${r.source}-${r.id}-${i}`} data-testid={`${testPrefix}-row-${r.id || i}`}>
                    {showSourceColumn && <TableCell><Badge className={r.source === 'lead' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-800'}>{r.source === 'lead' ? 'Lead' : 'Policy'}</Badge></TableCell>}
                    <TableCell className="font-medium">{r.name || '—'}</TableCell>
                    <TableCell>{r.insurer || '—'}</TableCell>
                    <TableCell>{r.product_type || r.product_name || '—'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(r.premium)}</TableCell>
                    <TableCell><Badge className="bg-slate-100 text-slate-800">{r.status || '—'}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{(r.renewed_at || r.lost_at || r.created_at || '').slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
